import type { ErrorMessage } from '@/core/api/error-messages';

/**
 * Todos estos son rechazos: la operación no se completó. Iban en amarillo, que sugiere que algo
 * pasó a medias y deja al administrador sin saber si tiene que repetirla. Solo queda en `info`
 * lo que de verdad informa sin haber rechazado nada.
 */

export const PLAN_ERRORS: Record<string, ErrorMessage> = {
  'Plan.NotFound': {
    title: 'Plan no encontrado',
    message: 'El plan solicitado no existe.',
    severity: 'error',
  },
  'Plan.NameRequired': {
    title: 'Nombre requerido',
    message: 'El nombre del plan es obligatorio.',
    severity: 'error',
  },
  'Plan.NameAlreadyExists': {
    title: 'Nombre duplicado',
    message: 'Ya existe un plan con ese nombre.',
    severity: 'error',
  },
  'Plan.InvalidPrice': {
    title: 'Precio inválido',
    message: 'Los precios mensual y anual deben ser mayores a cero.',
    severity: 'error',
  },
  'Plan.InvalidLimit': {
    title: 'Límite inválido',
    message: 'Los límites del plan no pueden ser negativos.',
    severity: 'error',
  },
  'Plan.PublicPlanLimitReached': {
    title: 'Solo puede haber tres planes públicos',
    message:
      'El onboarding muestra tres planes. Retira uno antes de activar otro, o crea este como privado.',
    severity: 'error',
  },
  'Plan.SyncFailed': {
    title: 'No se pudo sincronizar con Stripe',
    message:
      'El plan quedó guardado, pero sin precios en Stripe no se puede ofrecer. Reintenta la sincronización desde el detalle.',
    severity: 'error',
  },
  'Plan.AlreadySynced': {
    title: 'El plan ya está sincronizado',
    message: 'No hay nada pendiente de sincronizar.',
    severity: 'info',
  },
  'Plan.NotSynced': {
    title: 'El plan no está sincronizado',
    message: 'Sin precios en Stripe no hay con qué cobrarle a la cuenta.',
    severity: 'error',
  },
  'Plan.CannotMigrateWithoutSync': {
    title: 'No se puede aplicar a los clientes actuales',
    message:
      'El precio nuevo no llegó a Stripe, así que no hay a qué migrar. El precio del catálogo sí quedó guardado.',
    severity: 'error',
  },
  'Plan.PriceMigrationAlreadyScheduled': {
    title: 'Ya hay una migración agendada',
    message: 'Cancela la migración en curso antes de agendar otra para este plan.',
    severity: 'error',
  },
  'Plan.PriceMigrationNotFound': {
    title: 'Migración no encontrada',
    message: 'La migración de precio solicitada no existe.',
    severity: 'error',
  },
  'Plan.PriceMigrationNotCancellable': {
    title: 'La migración ya no se puede cancelar',
    message: 'Solo se puede detener una migración que siga agendada.',
    severity: 'error',
  },
  'Subscription.NotAdminAssigned': {
    title: 'La cuenta no tiene un plan asignado',
    message:
      'Esta suscripción la contrató la cuenta, así que se cancela por los caminos normales y no desde aquí.',
    severity: 'error',
  },
  'Subscription.TenantNotFound': {
    title: 'Cuenta no encontrada',
    message: 'La cuenta a la que intentas asignar el plan no existe.',
    severity: 'error',
  },
  // Sin esta entrada el 409 caia al generico por HTTP —"Ya existe un registro con estos
  // datos"—, redactado para nombres duplicados, que no dice nada de lo que de verdad pasa ni
  // de lo que hay que hacer.
  // El detalle de qué excede lo muestra el propio modal; este aviso solo orienta.
  'Subscription.AssignPlanExceedsLimits': {
    title: 'El plan queda por debajo de lo que la cuenta ya usa',
    message:
      'Revisa en el detalle qué recursos exceden y confirma si quieres asignarlo de todos modos.',
    severity: 'warning',
  },
  'Subscription.CannotAssignOverPaidSubscription': {
    title: 'La cuenta tiene una suscripción de pago activa',
    message: 'Cancélala en Stripe, o que la cuenta lo haga desde el SaaS, y vuelve a intentarlo.',
    severity: 'error',
  },
  'Subscription.NotFound': {
    title: 'La cuenta no tiene suscripción',
    message: 'No hay ninguna suscripción que revertir para esta cuenta.',
    severity: 'error',
  },
  'Cancellation.AlreadyScheduled': {
    title: 'La cancelación ya está agendada',
    message: 'Esta suscripción ya tiene una cancelación en curso.',
    severity: 'error',
  },
  'Cancellation.AlreadyCancelled': {
    title: 'La suscripción ya está cancelada',
    message: 'No hay nada que revertir.',
    severity: 'error',
  },
};
